// Явный импорт вместо авто-импорта Nuxt — после долгой серии HMR-правок за сессию
// авто-импорт для composable'ов, вызываемых из другого composable, иногда переставал
// резолвиться (см. такой же фикс и комментарий в useMainNavigation.ts:
// ReferenceError: useApi is not defined). Явный import от этого сканирования не зависит.
import { useApi } from './useApi'
import { stripAssistantMarkup, parseAssistantProgress, parseAssistantQuestion, type AssistantQuestion } from '../utils/assistantMarkup'

export interface ChatFabAttachment {
  name: string
  mediaType: string
  isImage: boolean
  size: number
  dataBase64?: string
  previewUrl?: string
}

export interface ChatFabMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  /** Если задано — именно этот текст уходит в API вместо `content` (нужно для
   * quick-action кнопок: в чате показываем короткую подпись, а ассистенту
   * отправляем развёрнутый контекст). */
  apiText?: string
  attachments?: ChatFabAttachment[]
}

export interface ChatFabProgress {
  status: 'idle' | 'running' | 'question' | 'done' | 'error'
  done: number | null
  total: number | null
  products: number | null
}

interface ChatFabAssistant {
  id: number
  name: string
  model: string
  provider: string
  has_api_key: boolean
}

export interface ChatFabConversation {
  id: number
  title: string
  updated_at: string
  assistant_name?: string
}

const HINT_DISMISSED_KEY = 'zh_chat_fab_hint_dismissed'

const ALLOWED_ATTACHMENT_EXT = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx']
const IMAGE_EXT = ['jpg', 'jpeg', 'png']
const EXT_MEDIA_TYPE: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
}
const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024
const MAX_ATTACHMENTS_PER_MESSAGE = 5

const CHAT_WIDTH_KEY = 'zh_chat_fab_width'
const DEFAULT_CHAT_WIDTH = 440
const MIN_CHAT_WIDTH = 340
const MAX_CHAT_WIDTH = 720

function loadStoredWidth(): number {
  try {
    const raw = Number(localStorage.getItem(CHAT_WIDTH_KEY))
    if (raw && raw >= MIN_CHAT_WIDTH && raw <= MAX_CHAT_WIDTH) return raw
  } catch { /* ignore */ }
  return DEFAULT_CHAT_WIDTH
}

/**
 * Часть стейта, нужная за пределами самого виджета (например layouts/default.vue
 * сжимает контент, когда чат открыт в полноразмерном режиме) — вынесена в
 * useState, чтобы быть общей между независимыми вызовами composable, и без
 * побочных эффектов/таймеров, которые должны жить только в одном экземпляре.
 */
export function useChatFabState() {
  const isOpen = useState('chatFab:isOpen', () => false)
  const isExpanded = useState('chatFab:isExpanded', () => false)
  const chatWidth = useState('chatFab:width', () => DEFAULT_CHAT_WIDTH)
  const isResizing = useState('chatFab:isResizing', () => false)

  function setChatWidth(px: number) {
    chatWidth.value = Math.round(Math.min(MAX_CHAT_WIDTH, Math.max(MIN_CHAT_WIDTH, px)))
  }

  function persistChatWidth() {
    try { localStorage.setItem(CHAT_WIDTH_KEY, String(chatWidth.value)) } catch { /* ignore */ }
  }

  return { isOpen, isExpanded, chatWidth, isResizing, setChatWidth, persistChatWidth }
}

/**
 * Стейт и логика виджета ChatFab. Компонент монтируется один раз глобально
 * (см. app.vue), поэтому большая часть стейта — обычные refs: он живёт, пока
 * смонтирован компонент, то есть на протяжении всей сессии пользователя в SPA
 * (переходы между страницами не пересоздают layout). Часть, нужная другим
 * компонентам (isOpen/isExpanded/chatWidth), вынесена в useChatFabState().
 */
export function useChatFab() {
  const { isOpen, isExpanded, chatWidth, isResizing, setChatWidth, persistChatWidth } = useChatFabState()
  const messages = ref<ChatFabMessage[]>([])
  const draft = ref('')
  const isStreaming = ref(false)
  const streamingText = ref('')

  // Прогресс работы ассистента и его вопрос с вариантами — из служебной разметки ответа
  // (utils/assistantMarkup.ts, протокол задаёт server/src/routes/chat.ts). Показываются
  // карточкой вместо приветствия и поверх игры в сайдбаре (ChatFab.vue, ChatSnakeGame.vue).
  const progress = ref<ChatFabProgress>({ status: 'idle', done: null, total: null, products: null })
  const pendingQuestion = ref<AssistantQuestion | null>(null)

  function applyAssistantMarkup(raw: string, finished: boolean) {
    const p = parseAssistantProgress(raw)
    if (p) progress.value = { ...progress.value, ...p }
    if (finished) {
      pendingQuestion.value = parseAssistantQuestion(raw)
      progress.value = { ...progress.value, status: pendingQuestion.value ? 'question' : 'done' }
    }
  }
  const assistants = ref<ChatFabAssistant[]>([])
  const assistantId = ref<number | null>(null)
  const assistantsLoaded = ref(false)
  const chatId = ref<number | null>(null)
  const unread = ref(0)
  const showHint = ref(false)

  // История чатов — список показывается прямо в теле панели (см. ChatFab.vue),
  // переключение туда-обратно локальным флагом, без отдельного роута/модалки.
  const showHistory = ref(false)
  const conversations = ref<ChatFabConversation[]>([])
  const loadingConversations = ref(false)

  async function openHistory() {
    showHistory.value = true
    loadingConversations.value = true
    try {
      conversations.value = await useApi<ChatFabConversation[]>('/chat/conversations')
    } catch (err) {
      console.error('[ChatFab] Failed to load conversations:', err)
    } finally {
      loadingConversations.value = false
    }
  }

  function closeHistory() {
    showHistory.value = false
  }

  async function openConversation(id: number) {
    try {
      const rows = await useApi<{ role: 'user' | 'assistant'; content: string; created_at: string }[]>(`/chat/conversations/${id}/messages`)
      messages.value = rows.map(r => makeMessage(r.role, r.role === 'assistant' ? stripAssistantMarkup(r.content) : r.content))
      // Прогресс и незакрытый вопрос — по последнему ответу ассистента в этом чате
      const last = rows[rows.length - 1]
      progress.value = { status: 'idle', done: null, total: null, products: null }
      pendingQuestion.value = null
      if (last?.role === 'assistant') applyAssistantMarkup(last.content, true)
      chatId.value = id
      showHistory.value = false
      scrollToBottom()
    } catch (err) {
      console.error('[ChatFab] Failed to load conversation messages:', err)
    }
  }

  function startNewConversation() {
    chatId.value = null
    messages.value = []
    progress.value = { status: 'idle', done: null, total: null, products: null }
    pendingQuestion.value = null
    showHistory.value = false
  }

  // Маленький (не полноразмерный) режим можно перетаскивать по странице и сворачивать
  // в компактную плашку — оба состояния локальны (единственный смонтированный экземпляр
  // ChatFab, см. app.vue), в useChatFabState() выносить незачем.
  const isMinimized = ref(false)
  const position = ref<{ x: number; y: number } | null>(null)
  const isDragging = ref(false)

  function minimize() {
    isMinimized.value = true
    // Сброс, а не перенос текущей позиции: она посчитана под габариты большой панели
    // (рамки перетаскивания завязаны на rect.width/height в момент захвата) и её
    // отражённая позиция может не подходить для маленькой плашки (например, оказаться
    // слишком близко к краю). Плашка стартует со своего дефолтного места (сверху справа);
    // потащат — снова запомнится, уже отдельно от полноразмерного режима.
    position.value = null
  }
  function restore() {
    isMinimized.value = false
    position.value = null
  }

  const pendingAttachments = ref<ChatFabAttachment[]>([])
  const attachmentError = ref('')

  const scrollEl = ref<HTMLElement | null>(null)
  const draftEl = ref<HTMLTextAreaElement | null>(null)
  const fileInputEl = ref<HTMLInputElement | null>(null)

  const activeAssistant = computed(() => assistants.value.find(a => a.id === assistantId.value) || null)
  const hasUsableAssistant = computed(() => !!activeAssistant.value?.has_api_key)

  let hintTimer: ReturnType<typeof setTimeout> | undefined

  function scrollToBottom() {
    nextTick(() => {
      if (scrollEl.value) scrollEl.value.scrollTop = scrollEl.value.scrollHeight
    })
  }

  function makeMessage(role: ChatFabMessage['role'], content: string, extra?: Partial<ChatFabMessage>): ChatFabMessage {
    return { id: `zh-fab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, role, content, ...extra }
  }

  async function ensureAssistants() {
    if (assistantsLoaded.value) return
    assistantsLoaded.value = true
    try {
      assistants.value = await useApi<ChatFabAssistant[]>('/assistants')
      const withKey = assistants.value.find(a => a.has_api_key)
      if (withKey) assistantId.value = withKey.id
    } catch (err) {
      console.error('[ChatFab] Failed to load assistants:', err)
    }
  }

  // Реагирует на isOpen независимо от того, кто его переключил — сама панель (раньше)
  // или внешняя кнопка в шапке (см. LayoutHeaderActions.vue), у которой нет доступа
  // к этим локальным refs (ensureAssistants/scrollEl/draftEl не расшарены через
  // useChatFabState, только isOpen). isOpen — глобальный useState, поэтому watch
  // здесь срабатывает на изменение из любого места.
  watch(isOpen, async (open) => {
    if (!open) return
    unread.value = 0
    await ensureAssistants()
    scrollToBottom()
    nextTick(() => draftEl.value?.focus())
  })

  function toggle() {
    isOpen.value = !isOpen.value
  }

  function open() {
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
    // isExpanded НЕ трогаем — по нему запоминается последний размер (большой/маленький),
    // чтобы при следующем открытии (см. LayoutHeaderActions.openAiChat) чат появлялся
    // в том же режиме, а не всегда полноразмерным. isMinimized же сбрасываем: закрыли
    // из свёрнутой плашки — при повторном открытии должна показаться сама панель,
    // а не снова плашка.
    isMinimized.value = false
  }

  function toggleExpand() {
    isExpanded.value = !isExpanded.value
    // Свёрнутая плашка — только для маленького режима, в полноразмерном не имеет смысла.
    if (isExpanded.value) isMinimized.value = false
  }

  /** Удаляет разговор из истории. Если он же сейчас открыт — сбрасывает
   * в чистый черновик (иначе можно было бы продолжать слать сообщения в
   * chatId, которого больше нет в БД), но саму историю не закрываем —
   * в отличие от startNewConversation(), удаление вызывается прямо из
   * списка истории и должно оставлять пользователя в нём. */
  async function deleteConversation(id: number) {
    try {
      await useApi(`/chat/conversations/${id}`, { method: 'DELETE' })
      conversations.value = conversations.value.filter(c => c.id !== id)
      if (chatId.value === id) {
        chatId.value = null
        messages.value = []
      }
    } catch (err) {
      console.error('[ChatFab] Failed to delete conversation:', err)
    }
  }

  function pickFile() {
    fileInputEl.value?.click()
  }

  function readFileAsAttachment(file: File): Promise<ChatFabAttachment | null> {
    const ext = (file.name.split('.').pop() || '').toLowerCase()
    if (!ALLOWED_ATTACHMENT_EXT.includes(ext)) {
      attachmentError.value = 'Можно прикрепить файлы форматов JPG, PNG, PDF, DOC или DOCX.'
      return Promise.resolve(null)
    }
    if (file.size > MAX_ATTACHMENT_SIZE) {
      attachmentError.value = `Файл «${file.name}» слишком большой — максимальный размер 5 МБ.`
      return Promise.resolve(null)
    }

    const isImage = IMAGE_EXT.includes(ext)
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        const base64 = result.slice(result.indexOf(',') + 1)
        resolve({
          name: file.name,
          mediaType: EXT_MEDIA_TYPE[ext] || file.type || 'application/octet-stream',
          isImage,
          size: file.size,
          dataBase64: base64,
          previewUrl: isImage ? URL.createObjectURL(file) : undefined,
        })
      }
      reader.onerror = () => {
        attachmentError.value = `Не удалось прочитать файл «${file.name}».`
        resolve(null)
      }
      reader.readAsDataURL(file)
    })
  }

  async function onFileChange(event: Event) {
    const input = event.target as HTMLInputElement
    const files = input.files ? Array.from(input.files) : []
    if (!files.length) return
    attachmentError.value = ''

    const room = MAX_ATTACHMENTS_PER_MESSAGE - pendingAttachments.value.length
    if (room <= 0) {
      attachmentError.value = `Можно прикрепить не больше ${MAX_ATTACHMENTS_PER_MESSAGE} файлов за раз.`
      input.value = ''
      return
    }
    const toRead = files.slice(0, room)
    if (files.length > toRead.length) {
      attachmentError.value = `Можно прикрепить не больше ${MAX_ATTACHMENTS_PER_MESSAGE} файлов за раз.`
    }

    const results = await Promise.all(toRead.map(readFileAsAttachment))
    for (const attachment of results) {
      if (attachment) pendingAttachments.value.push(attachment)
    }
    input.value = ''
  }

  function removeAttachment(index: number) {
    const [removed] = pendingAttachments.value.splice(index, 1)
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl)
    attachmentError.value = ''
  }

  /** Контент для API: вложения прикладываем только у самого свежего сообщения
   * (см. одноимённую логику на бэкенде) — старые вложения в истории не
   * пересылаем повторно, они и так уже увидены моделью в своём ответе. */
  function apiContentForMessage(message: ChatFabMessage, isLatest: boolean): any {
    const baseText = message.apiText ?? message.content
    if (isLatest && message.attachments?.length) {
      const blocks: any[] = []
      if (baseText.trim()) blocks.push({ type: 'text', text: baseText.trim() })
      for (const attachment of message.attachments) {
        blocks.push({
          type: 'attachment',
          name: attachment.name,
          mediaType: attachment.mediaType,
          isImage: attachment.isImage,
          dataBase64: attachment.dataBase64,
        })
      }
      return blocks
    }
    if (message.attachments?.length) {
      const names = message.attachments.map(a => `[Прикреплён файл: ${a.name}]`).join(' ')
      return `${baseText}\n\n${names}`.trim()
    }
    return baseText
  }

  async function runSend() {
    // chatId больше не создаётся заранее отдельным запросом (см. deleteConversation
    // и комментарий в server/src/routes/chat.ts) — бэкенд создаёт разговор лениво,
    // только когда реально получает сообщение пользователя, и присылает его id
    // первым SSE-событием ниже. Это исключает пустые "Новый чат" в истории, если
    // отправка не задалась (сеть/провайдер) до того, как что-то было сохранено.
    isStreaming.value = true
    streamingText.value = ''
    progress.value = { status: 'running', done: null, total: null, products: null }
    pendingQuestion.value = null
    let rawText = ''

    const config = useRuntimeConfig()
    // Same computation as ChatFab.vue's `tenderId` — re-evaluated per send so it tracks
    // whatever tender page (if any) the user is currently on. Used server-side to meter
    // the "N тендеров/месяц" AI plan quota; omitted (null) outside a tender page, which
    // the backend treats as untracked/unrestricted general chat.
    const route = useRoute()
    const currentTenderId = route.name === 'tenders-id' ? String(route.params.id) : null
    try {
      const payloadMessages = messages.value.map((m, idx) => ({
        role: m.role,
        content: apiContentForMessage(m, idx === messages.value.length - 1),
      }))

      // Вложения уже упакованы в payloadMessages — освобождаем base64 из
      // стейта, он больше не понадобится (повторно не пересылается).
      const lastMessage = messages.value[messages.value.length - 1]
      if (lastMessage?.attachments?.length) {
        for (const attachment of lastMessage.attachments) attachment.dataBase64 = undefined
      }

      const response = await fetch(`${config.public.apiBase}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          assistantId: assistantId.value,
          chatId: chatId.value,
          messages: payloadMessages,
          tenderId: currentTenderId,
        }),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(err.error || err.details || `HTTP ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') continue
            try {
              const parsed = JSON.parse(data)
              if (parsed.chatId) {
                // Разговор создан бэкендом только что (первое сообщение в сессии) —
                // запоминаем id, чтобы следующие сообщения продолжали этот же чат.
                chatId.value = parsed.chatId
              } else if (parsed.text) {
                rawText += parsed.text
                streamingText.value = stripAssistantMarkup(rawText)
                applyAssistantMarkup(rawText, false)
                scrollToBottom()
              } else if (parsed.error) {
                throw new Error(parsed.error)
              }
            } catch (e: any) {
              if (e.message && !e.message.includes('JSON')) throw e
            }
          }
        }
      }

      applyAssistantMarkup(rawText, true)
      const finalText = stripAssistantMarkup(rawText)
      if (finalText) {
        messages.value.push(makeMessage('assistant', finalText))
        if (!isOpen.value) unread.value += 1
      }
    } catch (err: any) {
      console.error('[ChatFab] Chat error:', err)
      progress.value = { ...progress.value, status: 'error' }
      messages.value.push(makeMessage('assistant', `Ошибка: ${err.message}`))
      if (!isOpen.value) unread.value += 1
    } finally {
      isStreaming.value = false
      streamingText.value = ''
      scrollToBottom()
    }
  }

  async function send() {
    const text = draft.value.trim()
    const attachments = pendingAttachments.value
    if ((!text && !attachments.length) || !assistantId.value || isStreaming.value) return

    messages.value.push(makeMessage('user', text, attachments.length ? { attachments: [...attachments] } : undefined))
    draft.value = ''
    pendingAttachments.value = []
    attachmentError.value = ''
    scrollToBottom()

    await runSend()
  }

  /** Используется quick-action кнопками (см. ChatFab.vue) — в чате видна
   * короткая подпись действия, а ассистенту уходит развёрнутый apiText. */
  async function sendQuickAction(displayText: string, apiText: string) {
    if (!assistantId.value || isStreaming.value) return
    messages.value.push(makeMessage('user', displayText, { apiText }))
    scrollToBottom()
    await runSend()
  }

  /** Ответ на вопрос ассистента одним из вариантов (кнопки в чате или поверх игры). */
  async function answerQuestion(option: string) {
    if (!assistantId.value || isStreaming.value || !option.trim()) return
    pendingQuestion.value = null
    messages.value.push(makeMessage('user', option.trim()))
    scrollToBottom()
    await runSend()
  }

  function onDraftKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      send()
    }
  }

  function dismissHint() {
    showHint.value = false
    try { localStorage.setItem(HINT_DISMISSED_KEY, '1') } catch { /* ignore */ }
  }

  onMounted(() => {
    chatWidth.value = loadStoredWidth()
  })

  onUnmounted(() => {
    if (hintTimer) clearTimeout(hintTimer)
    for (const attachment of pendingAttachments.value) {
      if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl)
    }
  })

  return {
    isOpen,
    isExpanded,
    chatWidth,
    isResizing,
    setChatWidth,
    persistChatWidth,
    messages,
    draft,
    isStreaming,
    streamingText,
    assistants,
    assistantId,
    assistantsLoaded,
    activeAssistant,
    hasUsableAssistant,
    chatId,
    unread,
    showHint,
    showHistory,
    conversations,
    loadingConversations,
    openHistory,
    closeHistory,
    openConversation,
    startNewConversation,
    deleteConversation,
    isMinimized,
    position,
    isDragging,
    minimize,
    restore,
    pendingAttachments,
    attachmentError,
    scrollEl,
    draftEl,
    fileInputEl,
    toggle,
    open,
    close,
    toggleExpand,
    send,
    sendQuickAction,
    progress,
    pendingQuestion,
    answerQuestion,
    onDraftKeydown,
    dismissHint,
    pickFile,
    onFileChange,
    removeAttachment,
  }
}
